import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  ChefHat,
  Tag,
  Eye,
  EyeOff,
  Grid3X3,
  List,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  Check,
  AlertCircle,
  Upload,
  X,
  Sparkles,
  Zap,
  Wand2,
  FileUp,
  Info,
  Layers,
  ChevronRight,
  ChevronDown,
  Printer,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useProducts, type Product, type Category } from '@/hooks/useProducts';
import { useKitchenStations } from '@/hooks/useKitchenStations';
import { useCompositeProducts } from '@/hooks/useCompositeProducts';
import { useAIImageGeneration } from '@/hooks/useAIImageGeneration';
import { supabase } from '@/lib/supabase';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useLocations, type LocationSettings } from '@/hooks/useLocations';
import { useLocationStore } from '@/store/location';
import { ProductTypeSelector, type ProductType } from '@/components/products/ProductTypeSelector';
import { LocationSelector } from '@/components/products/LocationSelector';
import { CompositeComponentEditor, type CompositeComponent, type ComponentProduct, type RawItem } from '@/components/products/CompositeComponentEditor';
import { ProductModifiersEditor } from '@/components/products/ProductModifiersEditor';
import { VariantAttributesEditor, type ProductVariantConfig } from '@/components/products/VariantAttributesEditor';
import { CompositeVariantMatrix, type VariantRecipeRow } from '@/components/products/CompositeVariantMatrix';
import { useProductVariants } from '@/hooks/useVariantAttributes';
import { ProductImportDialog, POSImportDialog } from '@/components/import';
import { ExpandedProductRow } from '@/components/products/ExpandedProductRow';

interface Toast {
  type: 'success' | 'error';
  message: string;
}

export default function ProductsList() {
  const {
    products,
    categories,
    isLoading,
    locationId,
    createProduct,
    updateProduct,
    deleteProduct,
    toggle86d,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useProducts();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { hasPermission } = usePermissions();
  const { activeStations } = useKitchenStations();
  const { profile } = useAuth();
  const { locations: availableLocations } = useLocations();
  const { currentLocation } = useLocationStore();
  
  // Get the default_track_inventory setting from the current location (defaults to true)
  const locationSettings = currentLocation?.settings as LocationSettings | null;
  const defaultTrackInventory = locationSettings?.default_track_inventory !== false;

  // Fetch raw materials for composite products
  const { data: rawMaterials } = useQuery({
    queryKey: ['raw-inventory-items', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return [];
      const { data, error } = await supabase
        .from('raw_inventory_items')
        .select('id, name, sku, cost_per_unit, unit_of_measure')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.organization_id,
  });

  // Fetch composite product virtual inventory (how many can be made)
  const { data: compositeInventory = [] } = useQuery({
    queryKey: ['composite-virtual-inventory', locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const { data, error } = await supabase
        .from('composite_product_virtual_inventory')
        .select('product_id, virtual_quantity, limiting_component, total_component_cost')
        .eq('location_id', locationId);
      if (error) {
        console.error('Error fetching composite inventory:', error);
        return [];
      }
      return data as { product_id: string; virtual_quantity: number; limiting_component: string | null; total_component_cost: number }[];
    },
    enabled: !!locationId,
  });

  // Build a lookup map for composite inventory by product_id
  const compositeInventoryMap = new Map(
    compositeInventory.map(ci => [ci.product_id, ci])
  );

  // Fetch suppliers for product supplier assignment
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
    enabled: !!profile?.organization_id,
  });
  const {
    credits,
    creditsLoading,
    isGenerating,
    generateImage,
    hasCredits,
  } = useAIImageGeneration();

  // Permission checks
  const canEditMenu = hasPermission('menu.edit');
  const canCreateMenu = hasPermission('menu.create');
  const canDeleteMenu = hasPermission('menu.delete');

  // URL params for deep linking (e.g., ?edit=product-id)
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledEditIdRef = useRef<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'sellable' | 'raw_goods'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showUnavailable, setShowUnavailable] = useState(true);
  const [toast, setToast] = useState<Toast | null>(null);

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Product dialog state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDialogStep, setProductDialogStep] = useState<'select_type' | 'edit'>('select_type');
  const [selectedProductType, setSelectedProductType] = useState<ProductType | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    cost: '',
    sku: '',
    sku_type: 'auto-generated',
    barcode: '',
    category_id: 'uncategorized',
    station_id: '', // Empty means use category-based routing
    send_to_kitchen: false, // Whether this product is routed to KDS
    supplier_id: '', // Primary supplier for this product
    is_available: true,
    is_sellable: true, // true = Sellable (POS), false = Raw Goods (inventory only)
    track_inventory: true, // Will be overridden by defaultTrackInventory in resetProductForm
    low_stock_threshold: '',
    par_level: '',
    image_url: '',
    product_type: 'item' as 'item' | 'modifier' | 'bundle' | 'composite' | 'variant' | 'composite_variant',
  });
  const [productSaving, setProductSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [skuGenerating, setSkuGenerating] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Per-location inventory levels for product creation (Lightspeed-style)
  const [inventoryLevels, setInventoryLevels] = useState<Record<string, { foh: string; boh: string; reorder_point: string; par_level: string }>>({});
  
  // Temporary components for new composite products (before save)
  const [tempComponents, setTempComponents] = useState<CompositeComponent[]>([]);
  
  // Variant product configuration
  const [variantConfig, setVariantConfig] = useState<ProductVariantConfig>({ attributes: [] });
  // Composite variant recipe matrix state
  const [variantRecipes, setVariantRecipes] = useState<VariantRecipeRow[]>([]);
  const {
    components: savedComponents,
    addComponent,
    updateComponent,
    removeComponent,
    saveComponents,
    saveVariantRecipes,
    totalComponentCost: savedComponentCost,
  } = useCompositeProducts(editingProduct?.id || null);
  const {
    generateAttributeCombinations,
  } = useProductVariants(editingProduct?.id || null);
  
  // Use saved components when editing, temp components when creating
  const displayComponents = editingProduct ? savedComponents : tempComponents;
  const displayTotalCost = editingProduct 
    ? savedComponentCost 
    : tempComponents.reduce((sum, c) => {
        if (c.componentProduct) {
          return sum + (c.componentProduct.cost || c.componentProduct.price) * c.quantity;
        }
        if (c.rawItem) {
          return sum + (c.rawItem.cost_per_unit || 0) * c.quantity;
        }
        return sum;
      }, 0);

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
  });
  const [categorySaving, setCategorySaving] = useState(false);

  // Inline category creation (from product form dropdown)
  const [inlineCategoryName, setInlineCategoryName] = useState('');
  const [inlineCategoryCreating, setInlineCategoryCreating] = useState(false);
  const [showInlineCategoryInput, setShowInlineCategoryInput] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [posImportSource, setPosImportSource] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'product' | 'category'; item: Product | Category } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [searchQuery, selectedCategory, productTypeFilter, showUnavailable]);

  // Handle URL params for deep linking to create, edit, duplicate, or import
  useEffect(() => {
    const createProduct = searchParams.get('create');
    const editProductId = searchParams.get('edit');
    const duplicateProductId = searchParams.get('duplicate');
    const importSource = searchParams.get('import');
    const posImportSourceParam = searchParams.get('posImport');

    if (createProduct === 'true' && createProduct !== handledEditIdRef.current) {
      handledEditIdRef.current = createProduct;
      setSearchParams({}, { replace: true });
      setTimeout(() => {
        openNewProduct();
      }, 0);
      return;
    }

    if (importSource === 'template' && importSource !== handledEditIdRef.current) {
      handledEditIdRef.current = importSource;
      setSearchParams({}, { replace: true });
      setTimeout(() => {
        setImportDialogOpen(true);
        handledEditIdRef.current = null;
      }, 0);
      return;
    }

    if (posImportSourceParam && posImportSourceParam !== handledEditIdRef.current) {
      handledEditIdRef.current = posImportSourceParam;
      setSearchParams({}, { replace: true });
      setTimeout(() => {
        setPosImportSource(posImportSourceParam);
        handledEditIdRef.current = null;
      }, 0);
      return;
    }
    
    // Handle edit param
    if (editProductId && editProductId !== handledEditIdRef.current && products && products.length > 0) {
      const productToEdit = products.find(p => p.id === editProductId);
      if (productToEdit) {
        // Mark this ID as handled to prevent re-triggering
        handledEditIdRef.current = editProductId;
        // Clear the URL param to prevent re-opening on navigation
        setSearchParams({}, { replace: true });
        // Open the edit dialog for this product (use setTimeout to ensure state is set)
        setTimeout(() => {
          openEditProduct(productToEdit);
        }, 0);
      }
    }
    
    // Handle duplicate param
    if (duplicateProductId && duplicateProductId !== handledEditIdRef.current && products && products.length > 0) {
      const productToDuplicate = products.find(p => p.id === duplicateProductId);
      if (productToDuplicate) {
        // Mark this ID as handled to prevent re-triggering
        handledEditIdRef.current = duplicateProductId;
        // Clear the URL param to prevent re-opening on navigation
        setSearchParams({}, { replace: true });
        // Create a duplicate with modified name
        setTimeout(() => {
          resetProductForm();
          setProductForm({
            name: `${productToDuplicate.name} (Copy)`,
            description: productToDuplicate.description || '',
            price: productToDuplicate.price?.toString() || '',
            cost: productToDuplicate.cost?.toString() || '',
            sku: '', // Clear SKU - it should be unique
            sku_type: productToDuplicate.sku_type || 'auto-generated',
            barcode: '', // Clear barcode - it should be unique
            category_id: productToDuplicate.category_id || 'uncategorized',
            station_id: productToDuplicate.station_id || '',
            send_to_kitchen: productToDuplicate.send_to_kitchen ?? false,
            supplier_id: productToDuplicate.supplier_id || '',
            is_available: productToDuplicate.is_available ?? true,
            is_sellable: productToDuplicate.is_sellable ?? true,
            track_inventory: productToDuplicate.track_inventory ?? true,
            low_stock_threshold: productToDuplicate.low_stock_threshold?.toString() || '',
            par_level: productToDuplicate.par_level?.toString() || '',
            image_url: productToDuplicate.image_url || '',
            product_type: productToDuplicate.product_type || 'item',
          });
          setEditingProduct(null); // This is a new product
          setProductDialogStep('edit');
          setProductDialogOpen(true);
        }, 0);
      }
    }
  }, [searchParams, products, setSearchParams]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  };

  // Generate a 5-digit numeric SKU automatically
  const handleGenerateSku = async () => {
    if (!profile?.organization_id) {
      showToast('error', 'Organization not found');
      return;
    }

    setSkuGenerating(true);
    try {
      const { data, error } = await supabase.rpc('generate_numeric_sku', {
        p_organization_id: profile.organization_id,
      });

      if (error) throw error;
      
      if (data) {
        setProductForm(prev => ({ ...prev, sku: data }));
      }
    } catch (error: any) {
      console.error('Error generating SKU:', error);
      showToast('error', error.message || 'Failed to generate SKU');
    } finally {
      setSkuGenerating(false);
    }
  };

  const filteredProducts = (products || []).filter((product) => {
    if (!showUnavailable && !product.is_available) return false;
    if (selectedCategory !== 'all' && product.category_id !== selectedCategory) return false;
    // Product type filter
    if (productTypeFilter === 'sellable' && product.is_sellable === false) return false;
    if (productTypeFilter === 'raw_goods' && product.is_sellable !== false) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Reset product form
  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: '',
      cost: '',
      sku: '',
      sku_type: 'auto-generated',
      barcode: '',
      category_id: 'uncategorized',
      station_id: '',
      send_to_kitchen: false,
      supplier_id: '',
      is_available: true,
      is_sellable: true, // Default to sellable
      track_inventory: defaultTrackInventory, // Use location setting (defaults to true)
      low_stock_threshold: '',
      par_level: '',
      image_url: '',
      product_type: 'item',
    });
    setEditingProduct(null);
    setSelectedProductType(null);
    setProductDialogStep('select_type');
    setTempComponents([]);
    setVariantConfig({ attributes: [] });
    setVariantRecipes([]);
    setInventoryLevels({});
    // Default to current location if available
    setSelectedLocationIds(locationId ? [locationId] : []);
    // Reset the deep link handler so the same product can be opened again via URL
    handledEditIdRef.current = null;
  };

  // Open product dialog for creating new product
  const openNewProduct = () => {
    resetProductForm();
    setProductDialogStep('select_type');
    setProductDialogOpen(true);
  };

  // Handle product type selection
  const handleProductTypeSelect = (type: ProductType) => {
    setSelectedProductType(type);
    // Map ProductType to database product_type
    const dbProductType = type === 'standard' ? 'item'
      : type === 'variant' ? 'variant'
      : type === 'composite_variant' ? 'composite_variant'
      : 'composite';
    setProductForm(prev => ({ ...prev, product_type: dbProductType }));
    setProductDialogStep('edit');
    // Auto-generate SKU for new products when type is auto-generated
    if (!editingProduct) {
      handleGenerateSku();
    }
  };

  // Open product dialog for editing
  const openEditProduct = async (product: Product) => {
    setEditingProduct(product);
    // Determine the ProductType from the database product_type
    const productType: ProductType = product.product_type === 'composite' ? 'composite'
      : product.product_type === 'composite_variant' ? 'composite_variant'
      : product.product_type === 'variant' ? 'variant'
      : 'standard';
    setSelectedProductType(productType);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      cost: product.cost?.toString() || '',
      sku: product.sku || '',
      sku_type: product.sku_type || 'auto-generated',
      barcode: product.barcode || '',
      category_id: product.category_id || 'uncategorized',
      station_id: product.station_id || '',
      send_to_kitchen: product.send_to_kitchen ?? false,
      supplier_id: product.supplier_id || '',
      is_available: product.is_available,
      is_sellable: product.is_sellable !== false, // Default to true if not set
      track_inventory: product.track_inventory,
      low_stock_threshold: product.low_stock_threshold?.toString() || '',
      par_level: product.par_level?.toString() || '',
      image_url: product.image_url || '',
      product_type: product.product_type || 'item',
    });
    
    // Fetch product's current locations
    try {
      const { data: productLocs } = await supabase
        .from('product_locations')
        .select('location_id')
        .eq('product_id', product.id);
      
      if (productLocs && productLocs.length > 0) {
        setSelectedLocationIds(productLocs.map(pl => pl.location_id));
      } else {
        // Fallback to product's primary location
        setSelectedLocationIds(product.location_id ? [product.location_id] : []);
      }
    } catch {
      // If product_locations doesn't exist yet, fallback to primary location
      setSelectedLocationIds(product.location_id ? [product.location_id] : []);
    }
    
    setProductDialogStep('edit');
    setProductDialogOpen(true);
  };

  // Available products for composite (exclude self and other composites)
  const availableComponentProducts: ComponentProduct[] = (products || [])
    .filter(p =>
      p.id !== editingProduct?.id &&
      p.product_type !== 'composite'
    )
    .map(p => ({
      id: p.id,
      name: p.name,
      price: p.price,
      cost: p.cost,
      sku: p.sku,
      image_url: p.image_url,
    }));

  // Available raw materials for composite products
  const availableRawItems: RawItem[] = (rawMaterials || []).map(r => ({
    id: r.id,
    name: r.name,
    sku: r.sku,
    cost_per_unit: r.cost_per_unit,
    unit_of_measure: r.unit_of_measure,
  }));

  // Temporary component management functions (for new products)
  const handleAddTempComponent = (itemId: string, quantity: number, isRawItem: boolean = false, rawItemData?: RawItem) => {
    if (isRawItem) {
      const rawItem = rawItemData || availableRawItems.find(r => r.id === itemId);
      if (!rawItem) return;

      // Check if already added
      if (tempComponents.some(c => c.rawItemId === itemId)) return;

      const newComponent: CompositeComponent = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        rawItemId: itemId,
        rawItem: rawItem,
        quantity,
        quantityUnit: rawItem.unit_of_measure,
      };
      setTempComponents(prev => [...prev, newComponent]);
    } else {
      const product = availableComponentProducts.find(p => p.id === itemId);
      if (!product) return;

      // Check if already added
      if (tempComponents.some(c => c.componentProductId === itemId)) return;

      const newComponent: CompositeComponent = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        componentProductId: itemId,
        componentProduct: product,
        quantity,
      };
      setTempComponents(prev => [...prev, newComponent]);
    }
  };

  const handleUpdateTempComponentQty = (componentId: string, quantity: number) => {
    setTempComponents(prev => 
      prev.map(c => c.id === componentId ? { ...c, quantity } : c)
    );
  };

  const handleRemoveTempComponent = (componentId: string) => {
    setTempComponents(prev => prev.filter(c => c.id !== componentId));
  };

  // Unified handlers that work for both edit and create
  const handleComponentAdd = (itemId: string, quantity: number, isRawItem: boolean = false, rawItemData?: RawItem) => {
    if (editingProduct) {
      if (isRawItem) {
        const rawItem = rawItemData || availableRawItems.find(r => r.id === itemId);
        addComponent.mutate({
          parentProductId: editingProduct.id,
          rawItemId: itemId,
          quantity,
          quantityUnit: rawItem?.unit_of_measure,
        });
      } else {
        addComponent.mutate({
          parentProductId: editingProduct.id,
          componentProductId: itemId,
          quantity,
        });
      }
    } else {
      handleAddTempComponent(itemId, quantity, isRawItem, rawItemData);
    }
  };

  const handleComponentUpdateQty = (componentId: string, quantity: number) => {
    if (editingProduct) {
      updateComponent.mutate({ componentId, quantity });
    } else {
      handleUpdateTempComponentQty(componentId, quantity);
    }
  };

  const handleComponentRemove = (componentId: string) => {
    if (editingProduct) {
      removeComponent.mutate(componentId);
    } else {
      handleRemoveTempComponent(componentId);
    }
  };

  // Handle file upload (core function that takes a File)
  const handleFileUpload = useCallback(async (file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      showToast('error', 'Invalid file type. Please use JPEG, PNG, WebP, or GIF.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showToast('error', 'File too large. Maximum size is 5MB.');
      return;
    }

    if (!locationId) {
      showToast('error', 'No location selected. Please select a location first.');
      return;
    }

    setImageUploading(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${locationId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(data.path);

      setProductForm(prev => ({ ...prev, image_url: publicUrl }));
      showToast('success', 'Image uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showToast('error', error.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  }, [locationId]);

  // Handle image upload from file input
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  // Remove image
  const handleRemoveImage = () => {
    setProductForm({ ...productForm, image_url: '' });
  };

  // Generate AI image
  const handleGenerateAIImage = async () => {
    if (!productForm.name.trim()) {
      showToast('error', 'Enter a product name first to generate an image');
      return;
    }

    if (!hasCredits) {
      showToast('error', 'No AI credits remaining. Upgrade your plan for more credits.');
      return;
    }

    const result = await generateImage({
      productName: productForm.name,
      description: productForm.description || undefined,
      productId: editingProduct?.id,
      locationId: locationId || undefined,
      style: 'food',
    });

    if (result.success && result.imageUrl) {
      setProductForm({ ...productForm, image_url: result.imageUrl });
      showToast('success', `Image generated! ${result.credits?.credits_remaining ?? 0} credits remaining`);
    } else if (result.upgradeRequired) {
      showToast('error', 'No AI credits remaining. Upgrade your plan for more credits.');
    } else {
      showToast('error', result.error || 'Failed to generate image');
    }
  };

  // Save product
  const handleSaveProduct = async () => {
    if (!productForm.name.trim()) {
      showToast('error', 'Product name is required');
      return;
    }

    if (!productForm.price || parseFloat(productForm.price) < 0) {
      showToast('error', 'Valid price is required');
      return;
    }

    // Use selected locations if available, otherwise fallback to current location
    const targetLocationIds = selectedLocationIds.length > 0 ? selectedLocationIds : (locationId ? [locationId] : []);
    
    if (targetLocationIds.length === 0) {
      showToast('error', 'Please select at least one location');
      return;
    }

    setProductSaving(true);
    try {
      // Auto-generate SKU if type is auto-generated and still empty
      let finalSku = productForm.sku.trim();
      if (productForm.sku_type === 'auto-generated' && !finalSku && profile?.organization_id) {
        const { data: generatedSku } = await supabase.rpc('generate_numeric_sku', {
          p_organization_id: profile.organization_id,
        });
        if (generatedSku) {
          finalSku = generatedSku;
          setProductForm(prev => ({ ...prev, sku: generatedSku }));
        }
      }

      // Use the first selected location as the primary location_id for backward compatibility
      const productData = {
        location_id: targetLocationIds[0],
        name: productForm.name.trim(),
        description: productForm.description.trim() || null,
        price: parseFloat(productForm.price),
        cost: productForm.cost ? parseFloat(productForm.cost) : null,
        sku: finalSku || null,
        sku_type: productForm.sku_type || 'auto-generated',
        barcode: productForm.barcode.trim() || null,
        category_id: productForm.category_id === 'uncategorized' ? null : productForm.category_id,
        station_id: productForm.station_id || null,
        send_to_kitchen: productForm.send_to_kitchen,
        supplier_id: productForm.supplier_id || null,
        is_available: productForm.is_available,
        is_sellable: productForm.is_sellable, // true = Sellable, false = Raw Goods
        track_inventory: productForm.track_inventory,
        low_stock_threshold: productForm.low_stock_threshold ? parseInt(productForm.low_stock_threshold) : null,
        par_level: productForm.par_level ? parseFloat(productForm.par_level) : null,
        is_active: true,
        sort_order: 0,
        product_type: productForm.product_type as 'item' | 'modifier' | 'bundle' | 'composite' | 'composite_variant',
        tax_rate: null,
        image_url: productForm.image_url || null,
      };

      if (editingProduct) {
        await updateProduct.mutateAsync({
          id: editingProduct.id,
          updates: productData,
        });
        
        // Sync product-location assignments: remove deselected, add/keep selected
        // First, delete locations that are no longer selected
        await supabase
          .from('product_locations')
          .delete()
          .eq('product_id', editingProduct.id)
          .not('location_id', 'in', `(${targetLocationIds.join(',')})`);

        // Then upsert the selected locations
        if (targetLocationIds.length > 0) {
          await supabase.rpc('add_product_to_locations', {
            p_product_id: editingProduct.id,
            p_location_ids: targetLocationIds,
          });
        }
        
        showToast('success', 'Product updated successfully');
      } else {
        // Build per-location inventory levels from the form
        const perLocationLevels = productForm.track_inventory
          ? targetLocationIds.map((locId) => {
              const lvl = inventoryLevels[locId];
              return {
                location_id: locId,
                quantity_foh: lvl?.foh ? parseFloat(lvl.foh) : 0,
                quantity_boh: lvl?.boh ? parseFloat(lvl.boh) : 0,
                reorder_point: lvl?.reorder_point ? parseInt(lvl.reorder_point) : (productForm.low_stock_threshold ? parseInt(productForm.low_stock_threshold) : null),
                reorder_quantity: lvl?.par_level ? parseFloat(lvl.par_level) : (productForm.par_level ? parseFloat(productForm.par_level) : null),
              };
            })
          : undefined;

        const newProduct = await createProduct.mutateAsync({
          ...productData,
          inventory_levels: perLocationLevels,
        });
        
        // Add product to additional locations (first one is already added via trigger)
        if (newProduct?.id && targetLocationIds.length > 1) {
          const additionalLocations = targetLocationIds.slice(1);
          await supabase.rpc('add_product_to_locations', {
            p_product_id: newProduct.id,
            p_location_ids: additionalLocations,
          });
        }
        
        // If this is a composite product with temp components, save them
        if (productForm.product_type === 'composite' && tempComponents.length > 0 && newProduct?.id) {
          const allComponents = tempComponents.map(c => ({
            componentProductId: c.componentProductId,
            rawItemId: c.rawItemId,
            quantity: c.quantity,
            quantityUnit: c.quantityUnit,
          }));
          if (allComponents.length > 0) {
            await saveComponents.mutateAsync({
              parentProductId: newProduct.id,
              components: allComponents,
            });
          }
        }

        // Composite Variant: save base recipe, generate variants, save per-variant recipes
        if (productForm.product_type === 'composite_variant' && newProduct?.id) {
          // 1. Save base recipe (variant_id = null)
          if (tempComponents.length > 0) {
            const baseComps = tempComponents.map(c => ({
              componentProductId: c.componentProductId,
              rawItemId: c.rawItemId,
              quantity: c.quantity,
              quantityUnit: c.quantityUnit,
            }));
            await saveComponents.mutateAsync({
              parentProductId: newProduct.id,
              components: baseComps,
              variantId: null,
            });
          }

          // 2. Generate variant names from attribute config
          const attrCombos = variantConfig.attributes
            .filter(a => a.values.length > 0)
            .map(a => ({
              name: a.attributeName,
              values: a.values.map(v => ({ id: v, value: v })),
            }));

          if (attrCombos.length > 0) {
            const combos = generateAttributeCombinations(attrCombos);

            // 3. Create variant records
            const createdVariants: { id: string; name: string; index: number }[] = [];
            for (let i = 0; i < combos.length; i++) {
              const combo = combos[i];
              const recipe = variantRecipes[i];
              const variantPrice = recipe ? parseFloat(recipe.price) : parseFloat(productForm.price) || 0;

              const { data: variant, error: vErr } = await supabase
                .from('product_variants')
                .insert({
                  product_id: newProduct.id,
                  name: combo.name,
                  price: variantPrice,
                  price_adjustment: 0,
                  sort_order: i,
                })
                .select()
                .single();

              if (!vErr && variant) {
                createdVariants.push({ id: variant.id, name: combo.name, index: i });

                // Link attribute values
                if (combo.valueIds.length > 0) {
                  await supabase
                    .from('product_variant_attribute_values')
                    .insert(combo.valueIds.map(vid => ({
                      variant_id: variant.id,
                      attribute_value_id: vid,
                    })));
                }
              }
            }

            // 4. Save per-variant component recipes
            if (createdVariants.length > 0 && tempComponents.length > 0) {
              const variantRecipeInputs = createdVariants.map(v => {
                const recipe = variantRecipes[v.index];
                return {
                  variantId: v.id,
                  components: tempComponents.map((c, compIdx) => ({
                    componentProductId: c.componentProductId,
                    rawItemId: c.rawItemId,
                    quantity: recipe
                      ? parseFloat(recipe.componentQuantities[compIdx] || c.quantity.toString()) || c.quantity
                      : c.quantity,
                    quantityUnit: c.quantityUnit,
                  })),
                };
              });

              await saveVariantRecipes.mutateAsync({
                parentProductId: newProduct.id,
                variantRecipes: variantRecipeInputs,
              });
            }

            // 5. Set product attributes
            const attrIds = variantConfig.attributes.map(a => a.attributeId).filter(Boolean);
            if (attrIds.length > 0) {
              await supabase
                .from('product_variant_attributes')
                .insert(attrIds.map((aid, idx) => ({
                  product_id: newProduct.id,
                  attribute_id: aid,
                  sort_order: idx,
                })));
            }
          }
        }
        
        showToast('success', 'Product created successfully');
      }

      setProductDialogOpen(false);
      resetProductForm();
    } catch (error: any) {
      console.error('Error saving product:', error);
      showToast('error', error.message || 'Failed to save product');
    } finally {
      setProductSaving(false);
    }
  };

  // Reset category form
  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '' });
    setEditingCategory(null);
  };

  // Save category
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      showToast('error', 'Category name is required');
      return;
    }

    if (!locationId) {
      showToast('error', 'No location selected');
      return;
    }

    setCategorySaving(true);
    try {
      const categoryData = {
        location_id: locationId,
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim() || null,
        parent_id: null,
        image_url: null,
        sort_order: 0,
        is_active: true,
      };

      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          updates: categoryData,
        });
        showToast('success', 'Category updated successfully');
      } else {
        await createCategory.mutateAsync(categoryData);
        showToast('success', 'Category created successfully');
      }

      setCategoryDialogOpen(false);
      resetCategoryForm();
    } catch (error: any) {
      console.error('Error saving category:', error);
      showToast('error', error.message || 'Failed to save category');
    } finally {
      setCategorySaving(false);
    }
  };

  // Create category inline from product form dropdown
  const handleInlineCategoryCreate = async () => {
    if (!inlineCategoryName.trim() || !locationId) return;
    setInlineCategoryCreating(true);
    try {
      const newCat = await createCategory.mutateAsync({
        location_id: locationId,
        name: inlineCategoryName.trim(),
        description: null,
        parent_id: null,
        image_url: null,
        sort_order: 0,
        is_active: true,
      });
      if (newCat) {
        setProductForm(prev => ({ ...prev, category_id: newCat.id }));
        showToast('success', `Category "${newCat.name}" created`);
      }
    } catch (error: any) {
      showToast('error', error.message || 'Failed to create category');
    } finally {
      setInlineCategoryCreating(false);
      setInlineCategoryName('');
      setShowInlineCategoryInput(false);
    }
  };

  // Create raw material inline from composite component editor
  const handleCreateRawItem = async (name: string): Promise<RawItem | null> => {
    if (!profile?.organization_id) return null;
    try {
      const { data, error } = await supabase
        .from('raw_inventory_items')
        .insert({
          organization_id: profile.organization_id,
          name,
          unit_of_measure: 'each',
          created_by: profile.id,
        })
        .select('id, name, sku, cost_per_unit, unit_of_measure')
        .single();

      if (error) throw error;
      // Refresh the raw materials list so the new item appears in searches
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      showToast('success', `Raw material "${name}" created`);
      return data as RawItem;
    } catch (error: any) {
      showToast('error', error.message || 'Failed to create raw material');
      return null;
    }
  };

  // Toggle product availability (86'd)
  const handleToggle86d = async (product: Product) => {
    try {
      await toggle86d.mutateAsync({
        id: product.id,
        is_available: !product.is_available,
      });
      showToast('success', product.is_available ? "Item marked as 86'd" : 'Item is now available');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to update availability');
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      if (deleteTarget.type === 'product') {
        await deleteProduct.mutateAsync(deleteTarget.item.id);
        showToast('success', 'Product deleted successfully');
      } else {
        await deleteCategory.mutateAsync(deleteTarget.item.id);
        showToast('success', 'Category deleted successfully');
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // Row expansion toggle
  const toggleRowExpansion = (productId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Bulk selection helpers
  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map((p) => p.id)));
    }
  };

  const toggleSelect = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const isAllSelected = filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length;
  const isSomeSelected = selectedProducts.size > 0 && selectedProducts.size < filteredProducts.length;

  // Bulk actions
  const handleBulkActivate = async () => {
    if (selectedProducts.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedProducts);
      const { error } = await supabase
        .from('products')
        .update({ is_available: true, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast('success', `${ids.length} product${ids.length > 1 ? 's' : ''} activated`);
      setSelectedProducts(new Set());
    } catch (error: any) {
      showToast('error', error.message || 'Failed to activate products');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedProducts.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedProducts);
      const { error } = await supabase
        .from('products')
        .update({ is_available: false, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast('success', `${ids.length} product${ids.length > 1 ? 's' : ''} deactivated`);
      setSelectedProducts(new Set());
    } catch (error: any) {
      showToast('error', error.message || 'Failed to deactivate products');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedProducts);
      const { error } = await supabase
        .from('products')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast('success', `${ids.length} product${ids.length > 1 ? 's' : ''} deleted`);
      setSelectedProducts(new Set());
    } catch (error: any) {
      showToast('error', error.message || 'Failed to delete products');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkUpdateCategory = async (categoryId: string) => {
    if (selectedProducts.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedProducts);
      const { error } = await supabase
        .from('products')
        .update({ category_id: categoryId, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const catName = categories?.find((c) => c.id === categoryId)?.name || 'selected category';
      showToast('success', `${ids.length} product${ids.length > 1 ? 's' : ''} moved to ${catName}`);
      setSelectedProducts(new Set());
    } catch (error: any) {
      showToast('error', error.message || 'Failed to update category');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkUpdateSupplier = async (supplierId: string) => {
    if (selectedProducts.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedProducts);
      const { error } = await supabase
        .from('products')
        .update({ supplier_id: supplierId, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const supplierName = suppliers?.find((s) => s.id === supplierId)?.name || 'selected supplier';
      showToast('success', `${ids.length} product${ids.length > 1 ? 's' : ''} assigned to ${supplierName}`);
      setSelectedProducts(new Set());
    } catch (error: any) {
      showToast('error', error.message || 'Failed to update supplier');
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <div>
      {/* Toast notification - z-[100] to be above modal overlays */}
      {toast && (
        <div
          className={cn(
            'fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-in slide-in-from-top-2 shadow-lg',
            toast.type === 'success' && 'bg-green-500 text-white',
            toast.type === 'error' && 'bg-red-500 text-white'
          )}
        >
          {toast.type === 'success' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <ChefHat className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{products?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Total Products</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-500/10">
                <Tag className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{categories?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-500/10">
                <Eye className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {products?.filter((p) => p.is_available).length || 0}
                </p>
                <p className="text-xs text-muted-foreground">Available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-red-500/10">
                <EyeOff className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {products?.filter((p) => !p.is_available).length || 0}
                </p>
                <p className="text-xs text-muted-foreground">86&apos;d Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Category Dropdown Filter */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories?.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Product Type Filter */}
        <Select value={productTypeFilter} onValueChange={(v) => setProductTypeFilter(v as 'all' | 'sellable' | 'raw_goods')}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="sellable">Sellable</SelectItem>
            <SelectItem value="raw_goods">Raw Goods</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setShowUnavailable(!showUnavailable)}
          >
            {showUnavailable ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showUnavailable ? "Hide 86'd" : "Show 86'd"}
          </Button>
          <div className="flex items-center rounded-lg border p-1">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                setViewMode('grid');
                setExpandedRows(new Set());
                setSelectedProducts(new Set());
              }}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          {hasPermission('menu.import_export') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <FileUp className="h-4 w-4" />
                  Import
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                  Import from BlazePOS Template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import from POS System</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setPosImportSource('lightspeed')}>
                  Import from Lightspeed
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  Import from Epay (Coming Soon)
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  Import from DimePOS (Coming Soon)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreateMenu && (
            <>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  resetCategoryForm();
                  setCategoryDialogOpen(true);
                }}
              >
                <FolderOpen className="h-4 w-4" />
                Add Category
              </Button>
              <Button
                variant="blaze"
                className="gap-2"
                onClick={openNewProduct}
              >
                <Plus className="h-4 w-4" />
                Add Product
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Products */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading products...</span>
          </div>
        </div>
      ) : !locationId ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No location selected</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Please select a location to manage your products
          </p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <ChefHat className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No products found</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {searchQuery ? 'Try a different search term' : 'Start by adding your first product'}
          </p>
          <Button
            variant="blaze"
            className="gap-2"
            onClick={openNewProduct}
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className={cn(
                'group relative overflow-hidden transition-all hover:shadow-lg',
                !product.is_available && 'opacity-60'
              )}
            >
              {/* Image */}
              <div className="aspect-square w-full bg-gradient-to-br from-blaze-100/50 to-blaze-50/50 dark:from-blaze-950/50 dark:to-blaze-900/30 relative">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-blaze-500/30" />
                  </div>
                )}
                {!product.is_available && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                    <Badge variant="destructive" className="text-xs">86&apos;d</Badge>
                  </div>
                )}
                {product.is_sellable === false && (
                  <div className="absolute top-1.5 left-1.5">
                    <Badge className="text-[10px] bg-amber-500/90 hover:bg-amber-500 text-white">Raw Goods</Badge>
                  </div>
                )}
                {(product.product_type === 'composite' || product.product_type === 'composite_variant') && (() => {
                  const ci = compositeInventoryMap.get(product.id);
                  if (!ci) return null;
                  return (
                    <div className="absolute top-1.5 right-1.5">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              className={cn(
                                "text-[10px] gap-0.5 cursor-help",
                                ci.virtual_quantity <= 0 && "bg-red-500/90 hover:bg-red-500 text-white",
                                ci.virtual_quantity > 0 && ci.virtual_quantity <= 5 && "bg-yellow-500/90 hover:bg-yellow-500 text-white",
                                ci.virtual_quantity > 5 && "bg-green-500/90 hover:bg-green-500 text-white"
                              )}
                            >
                              <Layers className="h-3 w-3" />
                              {ci.virtual_quantity}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">Can make {ci.virtual_quantity}</p>
                            {ci.limiting_component && (
                              <p className="text-xs text-muted-foreground">Limited by: {ci.limiting_component}</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  );
                })()}
              </div>

              <CardContent className="p-2.5">
                <div className="min-w-0 mb-1.5">
                  <h3 className="font-medium text-sm truncate">{product.name}</h3>
                </div>

                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 truncate max-w-[60%]">
                    {categories?.find((c) => c.id === product.category_id)?.name || 'Uncategorized'}
                  </Badge>
                  <span className="font-semibold text-xs text-primary">
                    {formatCurrency(product.effective_price ?? product.price)}
                  </span>
                </div>

                {(canEditMenu || canDeleteMenu) && (
                  <div className="flex gap-1.5 mt-2 pt-2 border-t">
                    {canEditMenu && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-7 text-xs gap-1"
                          onClick={() => openEditProduct(product)}
                        >
                          <Edit2 className="h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleToggle86d(product)}
                        >
                          {product.is_available ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      </>
                    )}
                    {canDeleteMenu && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => {
                          setDeleteTarget({ type: 'product', item: product });
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="w-10 p-3 pl-4">
                    <Checkbox
                      checked={isSomeSelected ? 'indeterminate' : isAllSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="w-8 p-3"></th>
                  {selectedProducts.size > 0 ? (
                    <th colSpan={5} className="text-left p-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2" disabled={bulkActionLoading}>
                            {bulkActionLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                            Choose an action ({selectedProducts.size} selected)...
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                          <DropdownMenuItem onClick={() => showToast('success', 'Print labels coming soon')}>
                            <Printer className="h-4 w-4 mr-2" />
                            Print Labels
                          </DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Update products
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>Update category</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {categories?.map((cat) => (
                                    <DropdownMenuItem key={cat.id} onClick={() => handleBulkUpdateCategory(cat.id)}>
                                      {cat.name}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>Update supplier</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {suppliers?.map((sup) => (
                                    <DropdownMenuItem key={sup.id} onClick={() => handleBulkUpdateSupplier(sup.id)}>
                                      {sup.name}
                                    </DropdownMenuItem>
                                  ))}
                                  {suppliers?.length === 0 && (
                                    <DropdownMenuItem disabled>No suppliers available</DropdownMenuItem>
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuItem onClick={() => showToast('success', 'Add to purchase order coming soon')}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add products to purchase order
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleBulkActivate}>
                            <Eye className="h-4 w-4 mr-2" />
                            Activate products
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleBulkDeactivate}>
                            <EyeOff className="h-4 w-4 mr-2" />
                            Deactivate products
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={handleBulkDelete}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete products
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </th>
                  ) : (
                    <>
                      <th className="text-left p-3 font-medium text-muted-foreground">Product</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Category</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Price</th>
                      <th className="text-center p-3 font-medium text-muted-foreground w-[100px]">Can Sell</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const isExpanded = expandedRows.has(product.id);
                  const isSelected = selectedProducts.has(product.id);
                  return (
                    <React.Fragment key={product.id}>
                      <tr
                        className={cn(
                          'border-b cursor-pointer transition-colors',
                          isExpanded ? 'bg-muted/30' : 'hover:bg-muted/50',
                          isSelected && 'bg-primary/5',
                          !product.is_available && 'opacity-60'
                        )}
                        onClick={() => toggleRowExpansion(product.id)}
                      >
                        <td className="p-3 pl-4" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(product.id)}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                        <td className="p-3 w-8">
                          <div className="shrink-0 w-5">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blaze-100/50 to-blaze-50/50 dark:from-blaze-950/50 dark:to-blaze-900/30 flex items-center justify-center shrink-0">
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <ChefHat className="h-4 w-4 text-blaze-500/50" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{product.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {product.sku || 'No SKU'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">
                            {categories?.find((c) => c.id === product.category_id)?.name || 'Uncategorized'}
                          </Badge>
                        </td>
                        <td className="p-3 font-semibold">
                          {formatCurrency(product.effective_price ?? product.price)}
                        </td>
                        <td className="p-3 text-center">
                          {(product.product_type === 'composite' || product.product_type === 'composite_variant') ? (() => {
                            const ci = compositeInventoryMap.get(product.id);
                            if (!ci) return <span className="text-xs text-muted-foreground">--</span>;
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 cursor-help">
                                      <span className={cn(
                                        "font-semibold tabular-nums",
                                        ci.virtual_quantity <= 0 && "text-red-600",
                                        ci.virtual_quantity > 0 && ci.virtual_quantity <= 5 && "text-yellow-600",
                                        ci.virtual_quantity > 5 && "text-green-600"
                                      )}>
                                        {ci.virtual_quantity}
                                      </span>
                                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-[240px]">
                                    <p className="font-medium mb-1">
                                      Can make {ci.virtual_quantity} unit{ci.virtual_quantity !== 1 ? 's' : ''}
                                    </p>
                                    {ci.limiting_component && (
                                      <p className="text-xs text-muted-foreground">
                                        Limited by: {ci.limiting_component}
                                      </p>
                                    )}
                                    {ci.total_component_cost > 0 && (
                                      <p className="text-xs text-muted-foreground">
                                        Component cost: {formatCurrency(ci.total_component_cost)}
                                      </p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })() : (
                            <span className="text-xs text-muted-foreground">--</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {product.is_sellable === false && (
                              <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-xs">Raw Goods</Badge>
                            )}
                            <Badge variant={product.is_available ? 'success' : 'destructive'}>
                              {product.is_available ? 'Available' : "86'd"}
                            </Badge>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded content row - Lightspeed-style tabbed view */}
                      {isExpanded && (
                        <ExpandedProductRow
                          product={product}
                          productId={product.id}
                          colSpan={7}
                          actions={{
                            onEdit: () => openEditProduct(product),
                            onToggle86d: canEditMenu ? () => handleToggle86d(product) : undefined,
                            onDuplicate: canCreateMenu ? () => setSearchParams({ duplicate: product.id }) : undefined,
                            onPrintLabels: () => showToast('success', 'Print labels coming soon'),
                            onViewMovements: () => navigate(`/stock-control/movements?product=${product.id}`),
                            onDelete: canDeleteMenu ? () => {
                              setDeleteTarget({ type: 'product', item: product });
                              setDeleteDialogOpen(true);
                            } : undefined,
                            canEdit: canEditMenu,
                            canDelete: canDeleteMenu,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Product Dialog */}
      <ResponsiveModal open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <ResponsiveModalContent maxWidth="3xl">
          {/* Step 1: Product Type Selection (only for new products) */}
          {!editingProduct && productDialogStep === 'select_type' ? (
            <>
              <ResponsiveModalHeader>
                <ResponsiveModalTitle>Add New Product</ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  Choose the type of product you want to create
                </ResponsiveModalDescription>
              </ResponsiveModalHeader>

              <ResponsiveModalBody>
                <ProductTypeSelector
                  selectedType={selectedProductType}
                  onSelect={handleProductTypeSelect}
                />
              </ResponsiveModalBody>

              <ResponsiveModalFooter>
                <Button
                  variant="outline"
                  onClick={() => setProductDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="blaze"
                  onClick={() => selectedProductType && setProductDialogStep('edit')}
                  disabled={!selectedProductType}
                >
                  Continue
                </Button>
              </ResponsiveModalFooter>
            </>
          ) : (
            <>
              {/* Step 2: Product Form */}
              <ResponsiveModalHeader>
                <ResponsiveModalTitle>
                  {editingProduct ? 'Edit Product' : `Add ${selectedProductType === 'variant' ? 'Variant' : selectedProductType === 'composite' ? 'Composite' : selectedProductType === 'composite_variant' ? 'Composite Variant' : 'Standard'} Product`}
                </ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  {editingProduct
                    ? 'Update the product details below'
                    : 'Fill in the product information to add it to your catalog'}
                </ResponsiveModalDescription>
              </ResponsiveModalHeader>

              <ResponsiveModalBody>
              <div className="grid gap-4">
                {/* Product Type Badge */}
                {!editingProduct && selectedProductType && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {selectedProductType === 'standard' ? 'Standard Product' : 
                       selectedProductType === 'variant' ? 'Variant Product' : 
                       selectedProductType === 'composite_variant' ? 'Composite + Variant' :
                       'Composite Product'}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => setProductDialogStep('select_type')}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      Change type
                    </button>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productName">Name *</Label>
                <Input
                  id="productName"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  placeholder="Product name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productCategory">Category</Label>
                {showInlineCategoryInput ? (
                  <div className="flex gap-2">
                    <Input
                      value={inlineCategoryName}
                      onChange={(e) => setInlineCategoryName(e.target.value)}
                      placeholder="New category name"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleInlineCategoryCreate();
                        }
                        if (e.key === 'Escape') {
                          setShowInlineCategoryInput(false);
                          setInlineCategoryName('');
                        }
                      }}
                      disabled={inlineCategoryCreating}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="blaze"
                      onClick={handleInlineCategoryCreate}
                      disabled={!inlineCategoryName.trim() || inlineCategoryCreating}
                      className="shrink-0"
                    >
                      {inlineCategoryCreating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowInlineCategoryInput(false);
                        setInlineCategoryName('');
                      }}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Select
                      value={productForm.category_id}
                      onValueChange={(value) => {
                        if (value === '__new__') {
                          setShowInlineCategoryInput(true);
                        } else {
                          setProductForm({ ...productForm, category_id: value });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uncategorized">Uncategorized</SelectItem>
                        {categories?.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__" className="text-primary font-medium">
                          + Add New Category
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Location Assignment */}
            {availableLocations.length > 1 && (
              <LocationSelector
                locations={availableLocations.map(l => ({ id: l.id, name: l.name }))}
                selectedLocations={selectedLocationIds}
                onSelectionChange={setSelectedLocationIds}
                disabled={productSaving}
              />
            )}

            {/* Send to Kitchen / KDS Toggle */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="sendToKitchen" className="text-sm font-medium">
                  Send to Kitchen Display
                </Label>
                <p className="text-xs text-muted-foreground">
                  Route this product to KDS/kitchen stations when ordered
                </p>
              </div>
              <Switch
                id="sendToKitchen"
                checked={productForm.send_to_kitchen}
                onCheckedChange={(checked) => setProductForm({ ...productForm, send_to_kitchen: checked })}
              />
            </div>

            {/* Kitchen Station Assignment - only shown when send_to_kitchen is on */}
            {productForm.send_to_kitchen && activeStations.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="productStation">Kitchen Station</Label>
                <Select
                  value={productForm.station_id}
                  onValueChange={(value) => setProductForm({ ...productForm, station_id: value === 'auto' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auto (use category routing)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (use category routing)</SelectItem>
                    {activeStations.map((station) => (
                      <SelectItem key={station.id} value={station.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: station.color || '#6366f1' }}
                          />
                          {station.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Override category-based routing by assigning this product to a specific KDS station
                </p>
              </div>
            )}

            {/* Supplier Assignment */}
            {suppliers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="productSupplier">Supplier</Label>
                <Select
                  value={productForm.supplier_id}
                  onValueChange={(value) => setProductForm({ ...productForm, supplier_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Primary supplier for this product (used for purchase order suggestions)
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="productDescription">Description</Label>
              <Textarea
                id="productDescription"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Product description"
                rows={2}
              />
            </div>

            {/* Product Image Upload with Drag & Drop */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Product Image</Label>
                <div className="flex items-center gap-1.5 text-xs">
                  <Zap className="h-3 w-3 text-amber-500" />
                  <span className="text-muted-foreground">
                    {creditsLoading ? (
                      'Loading credits...'
                    ) : credits ? (
                      `${credits.credits_remaining}/${credits.credits_limit} AI credits`
                    ) : (
                      '25 AI credits (free tier)'
                    )}
                  </span>
                </div>
              </div>
              <div
                className={cn(
                  "relative border-2 border-dashed rounded-lg p-4 transition-all",
                  isDragging
                    ? "border-primary bg-primary/5 scale-[1.01]"
                    : "border-border hover:border-primary/50 hover:bg-muted/30"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                {productForm.image_url ? (
                  <div className="flex items-center gap-4">
                    {/* Image Preview */}
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0 border">
                      <img
                        src={productForm.image_url}
                        alt="Product preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    
                    {/* Controls when image exists */}
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-medium">Image uploaded</p>
                      <p className="text-xs text-muted-foreground">Drag a new image to replace</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={imageUploading || isGenerating}
                          className="gap-2"
                        >
                          <Upload className="h-4 w-4" />
                          Replace
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateAIImage}
                          disabled={imageUploading || isGenerating || !hasCredits}
                          className="gap-2 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 hover:border-violet-500/50 hover:bg-violet-500/20"
                        >
                          <Sparkles className="h-4 w-4 text-violet-500" />
                          <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent font-medium">
                            Generate with AI
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    {imageUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                      </div>
                    ) : isGenerating ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-10 w-10 animate-spin text-violet-500" />
                        <p className="text-sm text-muted-foreground">Generating with AI...</p>
                      </div>
                    ) : (
                      <>
                        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
                          <Upload className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium mb-1">
                          {isDragging ? 'Drop image here' : 'Drag and drop an image'}
                        </p>
                        <p className="text-xs text-muted-foreground mb-3">
                          or use the buttons below (JPEG, PNG, WebP, GIF - max 5MB)
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            className="gap-2"
                          >
                            <Upload className="h-4 w-4" />
                            Upload
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGenerateAIImage}
                            disabled={!hasCredits}
                            className="gap-2 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 hover:border-violet-500/50 hover:bg-violet-500/20"
                          >
                            <Sparkles className="h-4 w-4 text-violet-500" />
                            <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent font-medium">
                              Generate with AI
                            </span>
                          </Button>
                        </div>
                        {!hasCredits && credits && credits.credits_remaining <= 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-500 flex items-center justify-center gap-1 mt-2">
                            <AlertCircle className="h-3 w-3" />
                            AI credits exhausted. Upgrade plan for more.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Product Type Selector */}
            <div className="space-y-2">
              <Label htmlFor="productType">Product Type</Label>
              <Select
                value={productForm.product_type}
                onValueChange={(value) => setProductForm({ ...productForm, product_type: value as any })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="item">Standard Item</SelectItem>
                  <SelectItem value="modifier">Modifier</SelectItem>
                  <SelectItem value="bundle">Bundle</SelectItem>
                  <SelectItem value="composite">Composite (Bill of Materials)</SelectItem>
                  <SelectItem value="composite_variant">Composite + Variant</SelectItem>
                </SelectContent>
              </Select>
              {productForm.product_type === 'composite' && (
                <p className="text-xs text-muted-foreground">
                  Composite products are made from other products. Inventory will be deducted from components when sold.
                </p>
              )}
              {productForm.product_type === 'composite_variant' && (
                <p className="text-xs text-muted-foreground">
                  Composite + Variant products combine recipes with size/attribute variants. Each variant can have different component quantities and its own price.
                </p>
              )}
            </div>

            {/* Composite Product Components (also for composite_variant base recipe) */}
            {(productForm.product_type === 'composite' || productForm.product_type === 'composite_variant' || selectedProductType === 'composite' || selectedProductType === 'composite_variant') && (
              <div className="p-4 rounded-lg border bg-muted/30">
                {(productForm.product_type === 'composite_variant' || selectedProductType === 'composite_variant') && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">1</span>
                    <div>
                      <p className="text-sm font-medium">Define the base recipe</p>
                      <p className="text-xs text-muted-foreground">Add the ingredients that make up this product. These quantities are the default for all variants.</p>
                    </div>
                  </div>
                )}
                <CompositeComponentEditor
                  components={displayComponents.map(c => {
                    const raw = c as any;
                    // Handle saved components (from DB) -- transform snake_case to camelCase
                    // Check for product component (via join or ID)
                    if (raw.component_product && raw.component_product.id) {
                      return {
                        id: c.id,
                        componentProductId: raw.component_product_id || raw.component_product.id,
                        componentProduct: {
                          id: raw.component_product.id,
                          name: raw.component_product.name,
                          price: raw.component_product.price,
                          cost: raw.component_product.cost,
                          sku: raw.component_product.sku || null,
                          image_url: raw.component_product.image_url,
                        },
                        quantity: c.quantity,
                      } as CompositeComponent;
                    }
                    // Handle saved raw item components (from DB)
                    if (raw.raw_item && raw.raw_item.id) {
                      return {
                        id: c.id,
                        rawItemId: raw.raw_item_id || raw.raw_item.id,
                        rawItem: {
                          id: raw.raw_item.id,
                          name: raw.raw_item.name,
                          cost_per_unit: raw.raw_item.cost_per_unit,
                          unit_of_measure: raw.raw_item.unit_of_measure,
                          sku: raw.raw_item.sku,
                        },
                        quantity: c.quantity,
                        quantityUnit: raw.quantity_unit || undefined,
                      } as CompositeComponent;
                    }
                    // DB component where only the ID reference exists but the join is null
                    // (e.g., the related product or raw item was deleted)
                    if (raw.component_product_id || raw.raw_item_id) {
                      return {
                        id: c.id,
                        componentProductId: raw.component_product_id || undefined,
                        rawItemId: raw.raw_item_id || undefined,
                        quantity: c.quantity,
                        quantityUnit: raw.quantity_unit || undefined,
                      } as CompositeComponent;
                    }
                    // Temp components (already in camelCase)
                    return c as CompositeComponent;
                  })}
                  availableProducts={availableComponentProducts}
                  availableRawItems={availableRawItems}
                  onAddComponent={handleComponentAdd}
                  onUpdateQuantity={handleComponentUpdateQty}
                  onRemoveComponent={handleComponentRemove}
                  onCreateRawItem={handleCreateRawItem}
                  disabled={productSaving}
                />
                {displayTotalCost > 0 && (
                  <div className="mt-4 pt-3 border-t flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Component Cost:</span>
                    <span className="font-medium">{formatCurrency(displayTotalCost)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Variant Product Attributes */}
            {(productForm.product_type === 'variant' || productForm.product_type === 'composite_variant' || selectedProductType === 'variant' || selectedProductType === 'composite_variant') && (
              <div className="p-4 rounded-lg border bg-muted/30">
                {(productForm.product_type === 'composite_variant' || selectedProductType === 'composite_variant') && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">2</span>
                    <div>
                      <p className="text-sm font-medium">Define variant sizes</p>
                      <p className="text-xs text-muted-foreground">Add an attribute (e.g. "Size") and its values (e.g. Small, Medium, Large). Each value becomes a variant with its own recipe quantities and price.</p>
                    </div>
                  </div>
                )}
                <VariantAttributesEditor
                  config={variantConfig}
                  onChange={setVariantConfig}
                  disabled={productSaving}
                  compositeVariantMode={productForm.product_type === 'composite_variant' || selectedProductType === 'composite_variant'}
                />
              </div>
            )}

            {/* Composite Variant Recipe Matrix */}
            {(productForm.product_type === 'composite_variant' || selectedProductType === 'composite_variant') && (
              <div className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">3</span>
                  <div>
                    <p className="text-sm font-medium">Adjust quantities per variant</p>
                    <p className="text-xs text-muted-foreground">Set how much of each ingredient goes into each variant, and set an independent price for each.</p>
                  </div>
                </div>
                <CompositeVariantMatrix
                  baseComponents={displayComponents.map(c => {
                    const raw = c as any;
                    if (raw.component_product && raw.component_product.id) {
                      return {
                        id: c.id,
                        componentProductId: raw.component_product_id || raw.component_product.id,
                        componentProduct: {
                          id: raw.component_product.id,
                          name: raw.component_product.name,
                          price: raw.component_product.price,
                          cost: raw.component_product.cost,
                          sku: raw.component_product.sku || null,
                          image_url: raw.component_product.image_url,
                        },
                        quantity: c.quantity,
                      } as CompositeComponent;
                    }
                    if (raw.raw_item && raw.raw_item.id) {
                      return {
                        id: c.id,
                        rawItemId: raw.raw_item_id || raw.raw_item.id,
                        rawItem: {
                          id: raw.raw_item.id,
                          name: raw.raw_item.name,
                          cost_per_unit: raw.raw_item.cost_per_unit,
                          unit_of_measure: raw.raw_item.unit_of_measure,
                          sku: raw.raw_item.sku,
                        },
                        quantity: c.quantity,
                        quantityUnit: raw.quantity_unit || undefined,
                      } as CompositeComponent;
                    }
                    return c as CompositeComponent;
                  })}
                  variantNames={(() => {
                    const attrs = variantConfig.attributes
                      .filter(a => a.values.length > 0)
                      .map(a => ({
                        name: a.attributeName,
                        values: a.values.map(v => ({ id: v, value: v })),
                      }));
                    if (attrs.length === 0) return [];
                    return generateAttributeCombinations(attrs).map(c => c.name);
                  })()}
                  variantRecipes={variantRecipes}
                  onChange={setVariantRecipes}
                  basePrice={productForm.price}
                  disabled={productSaving}
                />
              </div>
            )}

            {/* Product Modifiers - Only for standard items when editing */}
            {editingProduct && productForm.product_type === 'item' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Modifiers
                  <span className="text-xs font-normal text-muted-foreground">(customize with add-ons)</span>
                </Label>
                <div className="p-4 rounded-lg border bg-muted/30">
                  <ProductModifiersEditor
                    productId={editingProduct.id}
                    disabled={productSaving}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productPrice">Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="productPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="productCost">Cost Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="productCost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={productForm.cost}
                    onChange={(e) => setProductForm({ ...productForm, cost: e.target.value })}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>
            </div>

            {/* Profit Margin Display */}
            {(() => {
              const price = parseFloat(productForm.price) || 0;
              const cost = parseFloat(productForm.cost) || 0;
              if (price <= 0) return null;
              
              const profit = price - cost;
              const marginPercent = ((price - cost) / price) * 100;
              
              let marginColor = 'text-muted-foreground';
              let marginBg = 'bg-muted/50';
              if (cost === 0 && price > 0) {
                marginColor = 'text-amber-700 dark:text-amber-400';
                marginBg = 'bg-amber-50 dark:bg-amber-900/20';
              } else if (marginPercent < 0) {
                marginColor = 'text-red-700 dark:text-red-400';
                marginBg = 'bg-red-50 dark:bg-red-900/20';
              } else if (marginPercent >= 30) {
                marginColor = 'text-green-700 dark:text-green-400';
                marginBg = 'bg-green-50 dark:bg-green-900/20';
              }

              return (
                <div className={cn('rounded-lg p-3 flex items-center justify-between', marginBg)}>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Profit</p>
                      <p className={cn('font-semibold tabular-nums', marginColor)}>
                        {formatCurrency(profit)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Margin</p>
                      <p className={cn('font-semibold tabular-nums', marginColor)}>
                        {marginPercent.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  {cost === 0 && price > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">No cost entered</p>
                  )}
                  {marginPercent < 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400">Cost exceeds price</p>
                  )}
                </div>
              );
            })()}

            {/* SKU CODES */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">SKU Codes</h4>
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-4 items-start">
                <div className="space-y-2">
                  <Label htmlFor="skuCodeType">SKU code type</Label>
                  <Select
                    value={productForm.sku_type}
                    onValueChange={(value) => {
                      setProductForm(prev => ({
                        ...prev,
                        sku_type: value,
                        sku: value === 'auto-generated' ? '' : prev.sku,
                      }));
                      if (value === 'auto-generated') {
                        handleGenerateSku();
                      }
                    }}
                  >
                    <SelectTrigger id="skuCodeType">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto-generated">Auto-generated</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                      <SelectItem value="ean">EAN</SelectItem>
                      <SelectItem value="isbn">ISBN</SelectItem>
                      <SelectItem value="itf">ITF</SelectItem>
                      <SelectItem value="jan">JAN</SelectItem>
                      <SelectItem value="upc">UPC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="productSku">SKU code</Label>
                  <div className="flex gap-2">
                    <Input
                      id="productSku"
                      value={productForm.sku}
                      onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                      placeholder={productForm.sku_type === 'auto-generated' ? 'Auto-generated' : 'Enter SKU code'}
                      readOnly={productForm.sku_type === 'auto-generated'}
                      className={`flex-1 ${productForm.sku_type === 'auto-generated' ? 'bg-muted' : ''}`}
                    />
                    {productForm.sku_type === 'auto-generated' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleGenerateSku}
                        disabled={skuGenerating || !profile?.organization_id}
                        title="Regenerate SKU"
                      >
                        {skuGenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wand2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                {/* Preview */}
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-3">
                      {productForm.image_url ? (
                        <img src={productForm.image_url} alt="" className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                          <div className="h-4 w-4 rounded-sm bg-primary/20" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{productForm.name || 'Product name'}</p>
                        <p className="text-xs text-muted-foreground tabular-nums">{productForm.sku || '-----'}</p>
                      </div>
                    </div>
                    <hr className="border-border" />
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      The first SKU code will be shown to staff and customers to help identify this product. When you have multiple codes all the barcodes will be scannable.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Barcode */}
            <div className="space-y-2">
              <Label htmlFor="productBarcode">Barcode</Label>
              <Input
                id="productBarcode"
                value={productForm.barcode}
                onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })}
                placeholder="123456789"
                className="max-w-xs"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label htmlFor="productAvailable" className="font-medium">Available for sale</Label>
                <p className="text-sm text-muted-foreground">
                  Toggle off to mark as 86&apos;d (out of stock)
                </p>
              </div>
              <Switch
                id="productAvailable"
                checked={productForm.is_available}
                onCheckedChange={(checked) => setProductForm({ ...productForm, is_available: checked })}
                disabled={!productForm.is_sellable}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border bg-amber-50/50 dark:bg-amber-950/20">
              <div>
                <Label htmlFor="productSellable" className="font-medium">Product type</Label>
                <p className="text-sm text-muted-foreground">
                  {productForm.is_sellable 
                    ? 'Sellable - Appears on POS for customers to purchase' 
                    : 'Raw Goods - Tracked in inventory only, not sold on POS'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={cn(
                  "text-xs font-medium",
                  productForm.is_sellable ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400"
                )}>
                  Raw Goods
                </span>
                <Switch
                  id="productSellable"
                  checked={productForm.is_sellable}
                  onCheckedChange={(checked) => setProductForm({ 
                    ...productForm, 
                    is_sellable: checked,
                    // Auto-enable inventory tracking for raw goods
                    track_inventory: checked ? productForm.track_inventory : true,
                    // Raw goods are not available for sale
                    is_available: checked ? productForm.is_available : false,
                  })}
                />
                <span className={cn(
                  "text-xs font-medium",
                  productForm.is_sellable ? "text-green-700 dark:text-green-400" : "text-muted-foreground"
                )}>
                  Sellable
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label htmlFor="trackInventory" className="font-medium">Track inventory</Label>
                <p className="text-sm text-muted-foreground">
                  Enable inventory tracking for this product
                </p>
              </div>
              <Switch
                id="trackInventory"
                checked={productForm.track_inventory}
                onCheckedChange={(checked) => setProductForm({ ...productForm, track_inventory: checked })}
              />
            </div>

            {productForm.track_inventory && (
              <div className="space-y-4">
                {/* Per-location inventory levels (Lightspeed-style) */}
                {(() => {
                  const locationsForInventory = selectedLocationIds.length > 0
                    ? availableLocations.filter(l => selectedLocationIds.includes(l.id))
                    : locationId
                      ? availableLocations.filter(l => l.id === locationId)
                      : [];

                  if (locationsForInventory.length === 0) return null;

                  return (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                          Inventory Levels
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Set initial stock levels per location. FOH (Available to sell) is immediately sellable on POS.
                        </p>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Location</th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[100px]">
                                FOH
                                <span className="block text-[10px] font-normal">(Available to sell)</span>
                              </th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[100px]">
                                BOH
                                <span className="block text-[10px] font-normal">(Storage)</span>
                              </th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[100px]">Reorder Point</th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[100px]">PAR Level</th>
                            </tr>
                          </thead>
                          <tbody>
                            {locationsForInventory.map((loc) => {
                              const lvl = inventoryLevels[loc.id] || { foh: '', boh: '', reorder_point: '', par_level: '' };
                              const updateLevel = (field: string, value: string) => {
                                setInventoryLevels(prev => ({
                                  ...prev,
                                  [loc.id]: { ...prev[loc.id] || { foh: '', boh: '', reorder_point: '', par_level: '' }, [field]: value },
                                }));
                              };
                              return (
                                <tr key={loc.id} className="border-b last:border-0 hover:bg-muted/20">
                                  <td className="px-3 py-2 font-medium">{loc.name}</td>
                                  <td className="px-1 py-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={lvl.foh}
                                      onChange={(e) => updateLevel('foh', e.target.value)}
                                      placeholder="0"
                                      className="h-8 text-right text-sm tabular-nums"
                                    />
                                  </td>
                                  <td className="px-1 py-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={lvl.boh}
                                      onChange={(e) => updateLevel('boh', e.target.value)}
                                      placeholder="0"
                                      className="h-8 text-right text-sm tabular-nums"
                                    />
                                  </td>
                                  <td className="px-1 py-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={lvl.reorder_point}
                                      onChange={(e) => updateLevel('reorder_point', e.target.value)}
                                      placeholder="--"
                                      className="h-8 text-right text-sm tabular-nums"
                                    />
                                  </td>
                                  <td className="px-1 py-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={lvl.par_level}
                                      onChange={(e) => updateLevel('par_level', e.target.value)}
                                      placeholder="--"
                                      className="h-8 text-right text-sm tabular-nums"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Fallback: global low stock / PAR when no specific locations are set */}
                {selectedLocationIds.length === 0 && !locationId && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="lowStockThreshold">Low stock threshold</Label>
                      <Input
                        id="lowStockThreshold"
                        type="number"
                        min="0"
                        value={productForm.low_stock_threshold}
                        onChange={(e) => setProductForm({ ...productForm, low_stock_threshold: e.target.value })}
                        placeholder="Alert when below this"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parLevel">PAR Level</Label>
                      <Input
                        id="parLevel"
                        type="number"
                        min="0"
                        value={productForm.par_level}
                        onChange={(e) => setProductForm({ ...productForm, par_level: e.target.value })}
                        placeholder="Target stock level"
                      />
                      <p className="text-xs text-muted-foreground">
                        Ideal stock level for reordering
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
              </ResponsiveModalBody>

              <ResponsiveModalFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setProductDialogOpen(false);
                    resetProductForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="blaze"
                  onClick={handleSaveProduct}
                  disabled={productSaving}
                >
                  {productSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </Button>
              </ResponsiveModalFooter>
            </>
          )}
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Category Dialog */}
      <ResponsiveModal open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingCategory
                ? 'Update the category details below'
                : 'Create a new category to organize your products'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="categoryName">Name *</Label>
                <Input
                  id="categoryName"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Category name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoryDescription">Description</Label>
                <Textarea
                  id="categoryDescription"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Category description"
                  rows={3}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter className="gap-2">
            {editingCategory && (
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteTarget({ type: 'category', item: editingCategory });
                  setCategoryDialogOpen(false);
                  setDeleteDialogOpen(true);
                }}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setCategoryDialogOpen(false);
                resetCategoryForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleSaveCategory}
              disabled={categorySaving}
            >
              {categorySaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Dialog */}
      <ResponsiveModal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Confirm Delete</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Are you sure you want to delete &quot;{deleteTarget?.item.name}&quot;?
              {deleteTarget?.type === 'category' && (
                <span className="block mt-2 text-yellow-600">
                  Products in this category will become uncategorized.
                </span>
              )}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Product Import Dialog */}
      <ProductImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        categories={categories.map(c => ({ id: c.id, name: c.name }))}
      />

      {/* POS System Import Dialog */}
      {posImportSource && (
        <POSImportDialog
          open={!!posImportSource}
          onOpenChange={(open) => {
            if (!open) setPosImportSource(null);
          }}
          sourceId={posImportSource}
        />
      )}
    </div>
  );
}
